import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { waLink, waReason } from "@/lib/wa";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    MagnifyingGlass, Funnel, Hammer, ArrowRight, X,
    TrendUp, TrendDown, ChatCircleDots, CalendarBlank, UserCircle,
} from "@phosphor-icons/react";
import Sparkline from "@/components/doctor/Sparkline";
import PatientInsightsDrawer from "@/components/doctor/PatientInsightsDrawer";

/* AlignerInsights — second tab of the Aligner Dashboard.
   Shows aggregate compliance/wear analytics across the doctor's active cases.

   Live today : "Aligner changes till date" donut + compliance table
                (computed from current_aligner_stage + aligner_stage_updated_at).
   Coming soon: "Patient compliance" (needs wear-time tracker),
                "Aligner check-ins till date" (needs weekly photo check-ins),
                "Issues reported till date" (needs patient issue reporting).

   These three placeholders render an elegant muted donut with a "Live when X ships"
   tag, so the visual IA is locked in and the metrics flip on automatically as the
   patient-portal P1 features ship. */

const PRIMARY = "#009BA8";    // hsl(192 100% 31%)
const AMBER = "#F59E0B";      // amber-500
const AMBER_DARK = "#D97706"; // amber-600
const NEUTRAL = "#94A3B8";    // slate-400

const COMPLIANCE_CHIPS = [
    { key: "needs-attention", label: "Needs attention", dot: AMBER_DARK, tone: "bg-amber-100 text-amber-800 border-amber-200" },
    { key: "at-risk",         label: "At risk",         dot: AMBER,      tone: "bg-amber-50 text-amber-700 border-amber-200" },
    { key: "on-track",        label: "On track",        dot: PRIMARY,    tone: "bg-primary/10 text-primary border-primary/20" },
    { key: "pending",         label: "Pending updates", dot: NEUTRAL,    tone: "bg-foreground/5 text-foreground/80 border-foreground/15" },
];

// `daysSince` + `complianceOf` removed — replaced by backend-driven
// bucketing in /doctor/insights/compliance (wear-data driven, not
// stage-age-driven). Backend is the single source of truth.

// eslint-disable-next-line no-unused-vars
export default function AlignerInsights({ cases: _legacyCasesProp }) {
    // Aggregate counts for the 3 "wear / check-ins / issues" donuts come
    // from the legacy /doctor/insights/aligner endpoint (un-touched).
    const [insights, setInsights] = useState(null);
    // The first "Aligner pace" donut + the chip counts + the per-case table
    // all read from the new /doctor/insights/compliance endpoint so they're
    // a single source of truth with the same wear-driven bucket logic.
    const [compliance, setCompliance] = useState(null);
    const [loadingCompliance, setLoadingCompliance] = useState(true);

    useEffect(() => {
        api.get("/doctor/insights/aligner").then((r) => setInsights(r.data)).catch(() => setInsights(null));
    }, []);
    useEffect(() => {
        api.get("/doctor/insights/compliance")
            .then((r) => setCompliance(r.data))
            .catch(() => setCompliance(null))
            .finally(() => setLoadingCompliance(false));
    }, []);

    const items = compliance?.items || [];
    const buckets = compliance?.buckets || { "needs-attention": 0, "at-risk": 0, "on-track": 0, pending: 0 };
    const totalActive = buckets["on-track"] + buckets["at-risk"] + buckets["needs-attention"];
    const onTimePct = totalActive > 0 ? Math.round((buckets["on-track"] / totalActive) * 100) : 0;
    const practiceLocations = compliance?.practice_locations || [];

    // Filter / search state.
    // `?compliance=at-risk` (etc.) seeds the active chip so the Dashboard's
    // Compliance strip can deep-link a doctor straight to the filtered view.
    const [searchParams, setSearchParams] = useSearchParams();
    const allowedChips = new Set(COMPLIANCE_CHIPS.map((c) => c.key));
    const urlChip = searchParams.get("compliance");
    const [query, setQuery] = useState("");
    const [activeChip, setActiveChipState] = useState(allowedChips.has(urlChip) ? urlChip : null);
    const [activeLocId, setActiveLocId] = useState(null);
    const setActiveChip = (next) => {
        setActiveChipState(next);
        const params = new URLSearchParams(searchParams);
        if (next) params.set("compliance", next);
        else params.delete("compliance");
        setSearchParams(params, { replace: true });
    };

    // Drawer state — `?drawer=<patient_id>&case=<case_id>` deep-links
    // to the drawer (case_id pins to the exact case the doctor clicked,
    // not just whatever's "active" for that patient).
    const drawerPid = searchParams.get("drawer");
    const drawerCaseId = searchParams.get("case");
    const [drawer, setDrawer] = useState(null); // { patient_id, case_id, bucket } | null

    useEffect(() => {
        if (drawerPid) {
            const row = items.find((c) =>
                drawerCaseId ? c.case_id === drawerCaseId : c.patient_id === drawerPid
            );
            if (row) setDrawer({ patient_id: row.patient_id, case_id: row.case_id, bucket: row.bucket });
        }
    }, [drawerPid, drawerCaseId, items]);

    const openDrawer = (row) => {
        setDrawer({ patient_id: row.patient_id, case_id: row.case_id, bucket: row.bucket });
        const params = new URLSearchParams(searchParams);
        params.set("drawer", row.patient_id);
        params.set("case", row.case_id);
        setSearchParams(params, { replace: true });
    };
    const closeDrawer = () => {
        setDrawer(null);
        const params = new URLSearchParams(searchParams);
        params.delete("drawer");
        params.delete("case");
        setSearchParams(params, { replace: true });
    };

    const filteredItems = useMemo(() => {
        let list = items;
        if (activeChip) list = list.filter((c) => c.bucket === activeChip);
        if (activeLocId) list = list.filter((c) => c.practice_location_id === activeLocId);
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            list = list.filter((c) =>
                (c.patient_name || "").toLowerCase().includes(q) ||
                (c.patient_phone || "").toLowerCase().includes(q) ||
                (c.case_code || "").toLowerCase().includes(q));
        }
        return list;
    }, [items, query, activeChip, activeLocId]);

    return (
        <div className="space-y-6" data-testid="aligner-insights">
            {/* DONUT CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <DonutCard
                    title="Aligner pace"
                    centerValue={`${onTimePct}%`}
                    centerLabel="On time"
                    legend={[
                        { color: PRIMARY,    label: "On time",        value: buckets["on-track"] },
                        { color: AMBER,      label: "Delay < 7 days", value: buckets["at-risk"] },
                        { color: AMBER_DARK, label: "Delay > 7 days", value: buckets["needs-attention"] },
                    ]}
                    testid="donut-aligner-changes"
                />
                <DonutCard
                    title="Wear discipline"
                    centerValue={(() => {
                        const w = insights?.wear || {};
                        const ok = w.on_track || 0;
                        const totalScored = ok + (w.at_risk || 0) + (w.needs_attention || 0);
                        return totalScored > 0 ? `${Math.round(ok / totalScored * 100)}%` : "—";
                    })()}
                    centerLabel="On track"
                    legend={[
                        { color: PRIMARY,    label: "On track (≥20h)",        value: insights?.wear?.on_track || 0 },
                        { color: AMBER,      label: "At risk (16–19h)",       value: insights?.wear?.at_risk || 0 },
                        { color: AMBER_DARK, label: "Needs attention (<16h)", value: insights?.wear?.needs_attention || 0 },
                        { color: NEUTRAL,    label: "No data yet",            value: insights?.wear?.no_data || 0 },
                    ]}
                    testid="donut-compliance"
                />
                <DonutCard
                    title="Check-in streak"
                    centerValue={(() => {
                        const c = insights?.check_ins || {};
                        const ok = c.up_to_date || 0;
                        const total = ok + (c.missing_this_week || 0);
                        return total > 0 ? `${Math.round(ok / total * 100)}%` : "—";
                    })()}
                    centerLabel="This week"
                    legend={[
                        { color: PRIMARY, label: "Up to date",        value: insights?.check_ins?.up_to_date || 0 },
                        { color: AMBER,   label: "Missing this week", value: insights?.check_ins?.missing_this_week || 0 },
                    ]}
                    testid="donut-checkins"
                />
                <DonutCard
                    title="Reported concerns"
                    centerValue={String(insights?.issues?.total_open ?? 0)}
                    centerLabel="Open issues"
                    legend={[
                        { color: AMBER_DARK, label: "Missing aligner", value: insights?.issues?.by_type?.missing_aligner || 0 },
                        { color: AMBER,      label: "Broken aligner",  value: insights?.issues?.by_type?.broken_aligner || 0 },
                        { color: PRIMARY,    label: "Gum irritation",  value: insights?.issues?.by_type?.gum_irritation || 0 },
                        { color: NEUTRAL,    label: "Other",           value: (insights?.issues?.by_type?.sharp_edges || 0) + (insights?.issues?.by_type?.other || 0) },
                    ]}
                    testid="donut-issues"
                />
            </div>

            {/* SEARCH + FILTER ROW */}
            <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search patient, phone or clinic..."
                            className="h-10 pl-9"
                            data-testid="insights-search"
                        />
                    </div>
                    <Button variant="outline" size="sm" className="rounded-full h-10 px-5" disabled>
                        <Funnel size={14} className="mr-1.5" /> Filter
                    </Button>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mr-1">Compliance</span>
                    {COMPLIANCE_CHIPS.map((chip) => {
                        const isActive = activeChip === chip.key;
                        return (
                            <button
                                key={chip.key}
                                onClick={() => setActiveChip(isActive ? null : chip.key)}
                                data-testid={`compliance-chip-${chip.key}`}
                                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold border transition-all ${
                                    isActive
                                        ? `${chip.tone} ring-2 ring-offset-1 ring-primary/15`
                                        : `${chip.tone} opacity-70 hover:opacity-100`
                                }`}
                            >
                                <span className="w-2 h-2 rounded-full" style={{ background: chip.dot }} />
                                {chip.label}
                                <span className="text-[10px] tabular-nums opacity-80">({buckets[chip.key] || 0})</span>
                                {isActive && <X size={11} weight="bold" className="ml-0.5" />}
                            </button>
                        );
                    })}
                </div>

                {/* Practice-location filter chips — only render for multi-branch
                    clinics. Replaces the per-row "Practice location" column we
                    removed; users who care about branch can filter once and
                    the rest of the page reads correctly. */}
                {practiceLocations.length >= 2 && (
                    <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-border">
                        <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mr-1">Location</span>
                        <button
                            onClick={() => setActiveLocId(null)}
                            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold border transition-all ${
                                !activeLocId
                                    ? "bg-primary text-white border-primary"
                                    : "bg-card border-border text-muted-foreground hover:border-primary/40"
                            }`}
                            data-testid="location-chip-all"
                        >
                            All locations
                            <span className="text-[10px] tabular-nums opacity-80">({items.length})</span>
                        </button>
                        {practiceLocations.map((loc) => {
                            const active = activeLocId === loc.id;
                            return (
                                <button
                                    key={loc.id}
                                    onClick={() => setActiveLocId(active ? null : loc.id)}
                                    className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold border transition-all ${
                                        active
                                            ? "bg-primary text-white border-primary"
                                            : "bg-card border-border text-muted-foreground hover:border-primary/40"
                                    }`}
                                    data-testid={`location-chip-${loc.id}`}
                                >
                                    {loc.name}
                                    <span className="text-[10px] tabular-nums opacity-80">({loc.count})</span>
                                    {active && <X size={11} weight="bold" className="ml-0.5" />}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* COMPLIANCE TABLE — rebuilt to be ACTIONABLE per row.
                Each row answers: who, are they wearing aligners, where are
                they in the plan, when did I last engage them, what do I do
                about it. Contact + Practice location columns removed
                (location is now a filter chip; phone is reachable via the
                Nudge action). */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="hidden md:grid grid-cols-[1.5fr_1.4fr_1fr_1.1fr_1fr_1.2fr] gap-3 px-5 py-3 bg-secondary/40 border-b border-border text-[11px] font-bold tracking-[0.15em] uppercase text-foreground/70">
                    <div>Patient</div>
                    <div>Wear time (last 30d)</div>
                    <div>Stage</div>
                    <div>Last touch</div>
                    <div>Compliance</div>
                    <div className="text-right">Actions</div>
                </div>

                {loadingCompliance ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">Loading compliance data…</div>
                ) : filteredItems.length === 0 ? (
                    <div className="p-10 text-center text-sm text-muted-foreground" data-testid="insights-empty">
                        {items.length === 0
                            ? "No active patients in treatment yet. Compliance metrics will appear here once cases are delivered."
                            : <>No patients match your filter. <button onClick={() => { setActiveChip(null); setActiveLocId(null); setQuery(""); }} className="text-primary font-semibold underline">Clear filters</button></>}
                    </div>
                ) : (
                    <div className="divide-y divide-border" data-testid="insights-rows">
                        {filteredItems.map((c) => (
                            <ComplianceRow key={c.case_id} item={c} onOpenDrawer={() => openDrawer(c)} />
                        ))}
                    </div>
                )}

                {filteredItems.length > 0 && (
                    <div className="px-5 py-3 border-t border-border text-xs text-muted-foreground bg-secondary/30">
                        Showing <span className="font-semibold text-foreground">{filteredItems.length}</span> active {filteredItems.length === 1 ? "patient" : "patients"}
                    </div>
                )}
            </div>

            {/* Wave 3 — slide-in drawer with wear chart + stage history +
                issue timeline + nudge history. URL deep-links via ?drawer=<pid>&case=<cid>. */}
            <PatientInsightsDrawer
                open={!!drawer}
                patientId={drawer?.patient_id}
                caseId={drawer?.case_id}
                bucket={drawer?.bucket}
                onClose={closeDrawer}
            />
        </div>
    );
}


/* ─────────── Compliance row — actionable, wear-data driven ─────────── */
function ComplianceRow({ item, onOpenDrawer }) {
    const chip = COMPLIANCE_CHIPS.find((c) => c.key === item.bucket);
    const wear = item.wear_7d_avg;
    const trend = item.wear_trend;
    const series = item.wear_30d_series || [];
    const sparkColor = wear == null
        ? "#9ca3af"                 // grey when no data
        : wear < 12 ? "#dc2626"     // rose
            : wear < 16 ? "#d97706" // amber
                : "#059669";        // emerald

    // Pre-typed WhatsApp message uses the patient's first name. Same
    // wa.me click-to-chat pattern as PatientNudges (this is the same
    // surface, just exposed in a more analytic context). `waLink` returns
    // null for placeholder/invalid phones so the button stays disabled
    // with a tooltip instead of opening a dead link.
    const firstName = (item.patient_name || "").split(" ")[0] || "there";
    const nudgeBody = `Hi ${firstName}, this is a quick check-in from your aligner team. How are the aligners feeling today? Reply with a 👍 if everything's on track.`;
    const nudgeHref = waLink(item.patient_phone, nudgeBody);
    const nudgeBlockReason = waReason(item.patient_phone);
    const nudgeMsg = encodeURIComponent(nudgeBody);

    return (
        <div
            className="grid grid-cols-1 md:grid-cols-[1.5fr_1.4fr_1fr_1.1fr_1fr_1.2fr] gap-3 px-5 py-4 items-center hover:bg-secondary/30 transition-colors"
            data-testid={`insights-row-${item.case_id}`}
        >
            {/* Patient — click opens the Wave 3 deep-dive drawer */}
            <button
                onClick={onOpenDrawer}
                className="flex items-center gap-3 min-w-0 text-left group focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-lg"
                data-testid={`insights-row-open-drawer-${item.case_id}`}
                title="Open compliance deep-dive"
            >
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0 group-hover:bg-primary/15 transition">
                    {(item.patient_name || "P").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                    <div className="font-medium truncate group-hover:text-primary transition">{item.patient_name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground truncate font-mono">{item.case_code || "—"}</div>
                </div>
            </button>

            {/* Wear time — 7d avg + sparkline + trend arrow */}
            <div className="flex items-center gap-2.5" data-testid={`row-wear-${item.case_id}`}>
                <div className="shrink-0 text-right">
                    {wear != null ? (
                        <>
                            <div className="text-sm font-semibold tabular-nums">{wear}h</div>
                            <div className="text-[10px] text-muted-foreground tabular-nums">7d avg</div>
                        </>
                    ) : (
                        <>
                            <div className="text-sm text-muted-foreground/70">—</div>
                            <div className="text-[10px] text-muted-foreground">no logs</div>
                        </>
                    )}
                </div>
                <div style={{ color: sparkColor }}>
                    <Sparkline data={series} width={80} height={28} targetLine={22} />
                </div>
                {trend === "up" && <TrendUp size={12} weight="bold" className="text-emerald-600 shrink-0" />}
                {trend === "down" && <TrendDown size={12} weight="bold" className="text-rose-600 shrink-0" />}
            </div>

            {/* Stage progress — "12 / 24 · day 4 of 7" */}
            <div className="text-sm tabular-nums" data-testid={`row-stage-${item.case_id}`}>
                <span className="font-semibold">{item.stage || 0}</span>
                <span className="text-muted-foreground"> / {item.total_stages || "—"}</span>
                {item.stage_day != null && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                        day {item.stage_day} of {item.stage_total_days}
                    </div>
                )}
            </div>

            {/* Last touch — appointment or chat. Helps the doctor avoid
                pinging someone they spoke to yesterday. */}
            <LastTouch ts={item.last_touch_at} type={item.last_touch_type} />

            {/* Compliance pill — bucket from backend */}
            <div>
                {chip && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${chip.tone}`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: chip.dot }} />
                        {chip.label}
                    </span>
                )}
            </div>

            {/* Quick actions */}
            <div className="flex items-center justify-end gap-1.5" data-testid={`row-actions-${item.case_id}`}>
                {nudgeHref ? (
                    <a
                        href={nudgeHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                            // Fire-and-forget: log the nudge so it appears in
                            // the patient's history surface. We intentionally
                            // do not await — opening WhatsApp must not wait
                            // on a network round-trip, and a missed log is
                            // strictly better than a missed nudge.
                            api.post("/doctor/nudges", {
                                patient_id: item.patient_id,
                                case_id: item.case_id,
                                reason: item.compliance_reason || item.bucket || "manual_nudge",
                                message: decodeURIComponent(nudgeMsg),
                                channel: "whatsapp",
                            }).catch(() => { /* non-critical */ });
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                        title="Send a WhatsApp nudge"
                        data-testid={`row-nudge-${item.case_id}`}
                    >
                        <ChatCircleDots size={11} weight="duotone" /> Nudge
                    </a>
                ) : (
                    <span
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-zinc-100 text-muted-foreground border border-zinc-200 cursor-not-allowed"
                        title={nudgeBlockReason || "WhatsApp unavailable"}
                        data-testid={`row-nudge-disabled-${item.case_id}`}
                    >
                        <ChatCircleDots size={11} weight="duotone" /> Nudge
                    </span>
                )}
                <Link
                    to={`/doctor/patients/${item.patient_id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-card hover:bg-secondary border border-border text-foreground"
                    title="Open patient profile"
                    data-testid={`row-open-${item.case_id}`}
                >
                    <UserCircle size={11} weight="duotone" /> Open
                </Link>
                <Link
                    to="/doctor/appointments"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-card hover:bg-secondary border border-border text-foreground"
                    title="Book a visit"
                    data-testid={`row-book-${item.case_id}`}
                >
                    <CalendarBlank size={11} weight="duotone" /> Book
                </Link>
            </div>
        </div>
    );
}


/* "Last touch" cell — humanised relative time + small icon for the source */
function LastTouch({ ts, type }) {
    if (!ts) {
        return <div className="text-xs text-muted-foreground/70">—</div>;
    }
    const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    let label;
    if (Number.isNaN(days)) label = "—";
    else if (days === 0) label = "Today";
    else if (days === 1) label = "Yesterday";
    else if (days < 7) label = `${days}d ago`;
    else if (days < 30) label = `${Math.floor(days / 7)}w ago`;
    else label = `${Math.floor(days / 30)}mo ago`;

    const typeLabel = type === "appointment" ? "Appt"
        : type === "patient_message" ? "Chat"
        : type === "nudge" ? "Nudge"
        : "—";

    return (
        <div className="text-xs">
            <div className="font-semibold">{label}</div>
            <div className="text-[10px] text-muted-foreground">{typeLabel}</div>
        </div>
    );
}

/* ─────── Donut chart card ─────── */
function DonutCard({ title, centerValue, centerLabel, legend = [], muted, placeholderHint, placeholderLegend = [], testid }) {
    return (
        <div className="relative bg-card border border-border rounded-2xl p-5 flex flex-col" data-testid={testid}>
            {/* Title row with inline SOON badge */}
            <div className="flex items-start justify-between gap-2 mb-4 min-h-[36px]">
                <h3 className="text-sm font-semibold leading-snug flex-1">{title}</h3>
                {muted && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold tracking-[0.15em] uppercase shrink-0">
                        <Hammer size={9} weight="fill" /> Soon
                    </span>
                )}
            </div>

            {/* Donut + center label, centered */}
            <div className="flex flex-col items-center gap-4 flex-1">
                <Donut segments={muted ? [] : legend} size={130}>
                    {muted ? (
                        <div className="font-display text-2xl font-bold tabular-nums text-muted-foreground/60">—</div>
                    ) : (
                        <>
                            <div className="font-display text-2xl font-bold tabular-nums">{centerValue}</div>
                            {centerLabel && <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">{centerLabel}</div>}
                        </>
                    )}
                </Donut>

                {/* Legend stacked below donut, full width — no more truncation */}
                <div className="w-full space-y-1.5">
                    {muted
                        ? placeholderLegend.map((label) => (
                            <div key={label} className="flex items-center gap-2 text-xs text-muted-foreground/80">
                                <span className="w-1.5 h-1.5 rounded-full bg-foreground/15 shrink-0" />
                                <span className="tabular-nums text-foreground/50 font-semibold">0</span>
                                <span className="truncate">{label}</span>
                            </div>
                        ))
                        : legend.map((s) => (
                            <div key={s.label} className="flex items-center gap-2 text-xs">
                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.color }} />
                                <span className="tabular-nums font-semibold w-6">{s.value || 0}</span>
                                <span className="text-muted-foreground">{s.label}</span>
                            </div>
                        ))
                    }
                </div>
            </div>

            {muted && placeholderHint && (
                <div className="mt-4 pt-3 border-t border-dashed border-border text-[10px] italic text-muted-foreground">
                    {placeholderHint}
                </div>
            )}
        </div>
    );
}

/* ─────── Pure SVG donut. No external dependency. ─────── */
function Donut({ segments, size = 120, children }) {
    const stroke = 12;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const total = segments.reduce((s, sg) => s + (sg.value || 0), 0);
    const safeTotal = total || 1;
    let offset = 0;

    return (
        <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90 block">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="hsl(var(--border))"
                    strokeWidth={stroke}
                />
                {total > 0 && segments.map((seg, i) => {
                    const len = ((seg.value || 0) / safeTotal) * c;
                    if (len <= 0) return null;
                    const el = (
                        <circle
                            key={i}
                            cx={size / 2}
                            cy={size / 2}
                            r={r}
                            fill="none"
                            stroke={seg.color}
                            strokeWidth={stroke}
                            strokeDasharray={`${len} ${c}`}
                            strokeDashoffset={-offset}
                            strokeLinecap="butt"
                        />
                    );
                    offset += len;
                    return el;
                })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
                {children}
            </div>
        </div>
    );
}
